import routes from 'router/routes';
import store from 'store';
import {SET_APP_LOADING} from 'store/modules/common/common';
import Vue from 'vue';
import VueRouter from 'vue-router';
import fetchStoreData from 'router/utils/fetchStoreData';
import {ServicePermissionError} from 'services/errors';
import NotFound from 'pages/errors/pages/NotFound';
import Forbidden from 'pages/errors/pages/Forbidden';
import appEventBus from 'utils/appEventBus';
import showLoginModalPopup from 'router/utils/showLoginModalPopup';
import {SET_PREV_ROUTE} from 'store/modules/routing/routing';
import routePostprocessor from 'router/routePostprocessor';
import dirtyTracker from 'legacy/dirtytracker';
import MPromptManager from 'legacy/modules/common/components/MPrompt/MPromptManager';
import translation from 'translation';
import DynamicTitle from 'dynamicTitle/DynamicTitle';
import HttpService from 'api/HttpService';
import ApiUnauthorizedError from 'utils/api/errors/ApiUnauthorizedError';
import ApiPermissionDeniedError from 'utils/api/errors/ApiPermissionDeniedError';
import ApiNotFoundError from 'utils/api/errors/ApiNotFoundError';
import checkRoutePermissions from 'router/utils/checkRoutePermissions';
import {DEFAULT_ACCOUNT_ROUTE_NAME, DEFAULT_PROJECT_ROUTE_NAME} from 'router/constants';
import {isCurrentAppDefault} from 'utils/appTypeState';


async function fetchAllData (to, next) {
  try {
    await fetchStoreData(to);
  } catch (e) {
    if (e instanceof ServicePermissionError || e instanceof ApiPermissionDeniedError) {
      return next({name: '403page', replace: true, params: {0: to.path}});
    } else if (e instanceof ApiNotFoundError) {
      return next({name: '404page', replace: true, params: {0: to.path}});
    }
    throw e;
  }
}

async function dirtyPrompt () {
  await MPromptManager.show({
    headerText: translation.tr('You have unsaved changes'),
    bodyText: translation.tr('Are you sure you want to leave this page?'),
    cancelText: translation.tr('Cancel'),
    okText: translation.tr('Yes')
  });
}

async function dirtyCallback () {
  if (dirtyTracker.dirtyCount > 0 || store.getters['dirtyTracker/isDirty']) {
    await dirtyPrompt();
    store.commit('dirtyTracker/UNDIRTY_ALL');
    dirtyTracker.reset();
  }
}

let router = null;

if (isCurrentAppDefault()) {
  Vue.use(VueRouter);

  router = new VueRouter({
    mode: 'hash',
    routes: [
      ...routes,

      // ERROR PAGES
      {
        path: '*',
        name: '404page',
        component: NotFound
      },
      {
        path: '*',
        name: '403page',
        component: Forbidden
      }
    ]
  });

  dirtyTracker.on('change', (dirty) => {
    window.onbeforeunload = dirty ? function () {
      return dirtyTracker.unsavedMessage();
    } : null;
  });

  // router before all
  router.beforeEach(async (to, from, next) => {
    const fromPath = from.path;
    const toPath = to.path;

    if (fromPath !== toPath) {
      try {
        await dirtyCallback();
      } catch (e) {
        if (e.message === 'CANCEL_CLICKED') {
          return;
        }
        throw e;
      }

      // check super admin requirements
      if (to.matched.some(record => record.meta.requiredSuperAdmin)) {
        if (!store.getters['user/currentUserIsAdmin']) {
          console.warn('You have no permission to view this page');
          return next({name: '403page', replace: true, params: {0: to.path}});
        }
      }

      // redirect from path 'publicis' to path 'rore'
      if (to.path.includes('/publicis/')) {
        const newPath = to.path.replace('/publicis/', '/rore/');
        next({
          path: newPath,
          replace: true,
          query: to.query
        });
      }

      // check aori
      if (to.matched.some(record => record.meta.aoriOnly)) {
        if (!store.getters['common/isAori']) {
          console.warn('Page not found');
          return next({
            name: '404page',
            replace: true,
            params: {0: to.path}
          });
        }
      }

      HttpService.cancelAllRequests();

      store.dispatch(`common/${SET_APP_LOADING}`, true);

      try {
        await fetchAllData(to, next);
      } catch (e) {
        if (e instanceof ApiUnauthorizedError) {
          await showLoginModalPopup();

          // retry fetch after successful login
          await fetchAllData(to, next);
        } else if (e.name === 'RequestCanceledError') {
          return next(false);
        } else {
          throw e;
        }
      }
      store.dispatch(`common/${SET_APP_LOADING}`, false);

      // check navigation in sidebar
      if (to.name === from.name) {
        // if check permission failed redirect to default account/project route
        if (!checkRoutePermissions(to)) {

          console.warn(
            `Current account/project has not permission for route [${to.name}]. Redirecting to default route`
          );

          // account has one param and it is accountId
          const isAccountRoute = Object.keys(to.params).length === 1 && Boolean(to.params.accountId);

          const isProjectRoute = Object.keys(to.params).length === 2 && Boolean(to.params.accountId) &&
            Boolean(to.params.projectId);

          let routeName = null;

          if (isAccountRoute) {
            routeName = DEFAULT_ACCOUNT_ROUTE_NAME;
          } else if (isProjectRoute) {
            routeName = DEFAULT_PROJECT_ROUTE_NAME;
          } else {
            console.error(`Failed to find fallback route for [${to.name}]`);
            return next({
              name: '404page',
              replace: true,
              params: {0: to.path}
            });
          }
          return next({
            name: routeName,
            replace: true,
            params: {...to.params}
          });
        }
      }

      // check account components
      if (to.matched.some(record => record.meta.requiresComponents)) {
        const componentsList = to.matched.map(record => record.meta.requiresComponents).filter(c => Boolean(c));

        // false if at least one component is not enabled in target account
        const hasComponents = !componentsList.some(c => !store.getters['accounts/hasComponent'](c));
        if (!hasComponents) {
          console.warn('Required component is disabled');
          return next({
            name: '404page',
            replace: true,
            params: {0: to.path}
          });
        }
      }

      if (to.meta) {
        // deal with usual account route redirects
        const meta = to.meta;
        const currentAccount = store.getters['accounts/currentAccount'];

        if (meta.usualAccountRedirectRoute && currentAccount && currentAccount.type === 'USUAL') {
          return next({
            name: meta.usualAccountRedirectRoute,
            replace: true,
            params: {...to.params}
          });
        }
      }

      appEventBus.$emit('ROUTE_DATA_FETCHED');
    }

    if (to.name !== '403page' && to.name !== '404page') {

      // do not write prev route or navigate back will not work on error pages
      store.dispatch(`routing/${SET_PREV_ROUTE}`, from);
    } else {
      DynamicTitle.updateRouteName(to.matched);
    }

    next();
    appEventBus.$emit('ROUTE_NAVIGATION_COMPLETE');
  });

  router.afterEach((to) => {
    routePostprocessor();

    // track route navigation in google analytics
    if (window.ga) {
      window.ga('set', 'page', to.path);
      window.ga('send', 'pageview');
    }
  });
}

export default router;
